import React from 'react';
import t from 'prop-types';
import { Map } from 'immutable';
import classNames from 'classnames';
import icons from '@kineticdata/ui/assets/icons/icons.json';
import { pickBy } from 'lodash-es';

const ICONS = Map(icons);

const DEFAULT_BG = '#ECEDFE';

const DEFAULT_SVG_PROPS = {
  xmlns: 'http://www.w3.org/2000/svg',
  viewBox: '0 0 24 24',
  fill: 'none',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const SIZES = Map({
  sm: 16,
  md: 20,
  lg: 30,
  xl: 40,
  jumbo: 80,
  default: 24,
});

const getIconBody = name => ICONS.get(name, ICONS.get('404'));

// Get the size based on the props
const getDefaultSize = sizeProps =>
  SIZES.get(Object.keys(sizeProps)[0], SIZES.get('default'));

// Get the class based on the size, if it matches a standard size
const getSizeClass = size => {
  const suffix = SIZES.findKey((v, k) => v === size && k !== 'default');
  return suffix ? `icon-${suffix}` : null;
};

// Determine the stroke based on the size. This function keeps sets teh stroke
// to 2 at the default size of 24, and scales nicely as the icons grow/shrink.
const getDefaultStroke = size => Math.pow(24 / size, 0.4) * 2;

// If a part has its own stroke width, check to see if the value is a
// percentage, and if it is, calculate it relative to the svg stroke width.
const calculatePartStroke = (partStrokeWidth, strokeWidth) => {
  if (typeof partStrokeWidth === 'string' && partStrokeWidth.endsWith('%')) {
    const percentage = parseFloat(partStrokeWidth) / 100;
    if (!!percentage) {
      return strokeWidth * percentage;
    }
  }
  return partStrokeWidth;
};

// Get props for the current animation
const getAnimationProps = animProps => {
  // Get first animation prop
  const animation = Object.keys(animProps)[0];
  if (animation && animProps[animation] !== false) {
    return [
      // Return class for animation
      `icon-${animation}`,
      // Return iteration count if a number was provided
      typeof animProps[animation] === 'number'
        ? animProps[animation]
        : undefined,
    ];
  }
  return null;
};

export const Icon = ({
  name,
  className,
  color = 'currentColor',
  bg,
  svgProps = {},
  ...props
}) => {
  const iconBody = getIconBody(name);
  if (!Array.isArray(iconBody)) return null;

  const hasBg = !!bg;
  const bgColor = typeof bg === 'string' ? bg : DEFAULT_BG;

  // Get and calculate size props
  const {
    size = getDefaultSize(
      pickBy(
        props,
        (value, key) =>
          ['sm', 'md', 'lg', 'xl', 'jumbo'].includes(key) && value !== false,
      ),
    ),
    strokeWidth = getDefaultStroke(size),
    em,
  } = props;
  const sizeVal = `${size / 16}${em ? 'em' : 'rem'}`;

  // Get animation props
  const [animationClass, animationIterationCount] =
    getAnimationProps(
      pickBy(
        props,
        (value, key) =>
          ['spin', 'bounce', 'shake', 'beat', 'whirl'].includes(key) &&
          value !== false,
      ),
    ) || [];

  return (
    <svg
      className={classNames(
        'icon',
        getSizeClass(size),
        animationClass,
        className,
      )}
      {...DEFAULT_SVG_PROPS}
      width={sizeVal}
      height={sizeVal}
      stroke={color}
      strokeWidth={strokeWidth}
      data-name={name}
      {...svgProps}
      style={{ animationIterationCount, ...(svgProps.style || {}) }}
    >
      {iconBody
        .map(part => ({
          ...part,
          attrs: {
            ...part.attrs,
            // Set fill color if necessary
            fill:
              // If icon bg is enabled and the part supports a background
              // color, set the fill to the bgColor
              hasBg && part.bg
                ? bgColor
                : // If the part supports a fill, set it to the stroke color
                  part.fill === true
                  ? color
                  : // If the part has its own fill value, use that
                    part.attrs.fill,
            // Set the strokeWidth if a value was provided on this part
            strokeWidth: calculatePartStroke(
              part.attrs.strokeWidth,
              strokeWidth,
            ),
          },
        }))
        .map(({ tag: Tag, attrs }, i) => (
          <Tag key={`svg-part-${i}`} {...attrs} />
        ))}
    </svg>
  );
};

Icon.propTypes = {
  /** Name of icon to display. */
  name: t.string.isRequired,
  /** Additional classNames to add to the icon svg element. */
  className: t.string,
  /** Color of the icon stroke. */
  color: t.string,
  /**
   * Should the icon have a background. If string, sets the color of the
   * background. Not all icons support a background color.
   */
  bg: t.oneOfType([t.bool, t.string]),
  /**
   * Object of props that will be passed to the icon `svg` element, overriding
   * any default props.
   */
  svgProps: t.object,
  /**
   * Numeric value for size of the icon if custom sizes are needed, in pixels.
   * Overrides any of the predefined sizes. Default size is 24px if no sizes are
   * provided.
   */
  size: t.number,
  /** Predefined size for a small icon (16px). */
  sm: t.bool,
  /** Predefined size for a medium icon (20px). */
  md: t.bool,
  /** Predefined size for a large icon (30px). */
  lg: t.bool,
  /** Predefined size for a extra-large icon (40px). */
  xl: t.bool,
  /** Predefined size for a jumbo icon (80px). */
  jumbo: t.bool,
  /**
   * Width of the stroke. Defaults to an appropriate value based on the size of
   * the icon.
   */
  strokeWidth: t.number,
  /**
   * Flag that changes the svg size units from rem to em. Size should still be a
   * pixel value with 16 being equal to 1em.
   */
  em: t.bool,
  /**
   * Adds a spin animation to the icon. If a number value is set, it represents
   * the iteration count of the animation.
   */
  spin: t.oneOfType([t.bool, t.number]),
  /**
   * Adds a bounce animation to the icon. If a number value is set, it
   * represents the iteration count of the animation.
   */
  bounce: t.oneOfType([t.bool, t.number]),
  /**
   * Adds a shake animation to the icon. If a number value is set, it represents
   * the iteration count of the animation.
   */
  shake: t.oneOfType([t.bool, t.number]),
  /**
   * Adds a beat animation to the icon. If a number value is set, it represents
   * the iteration count of the animation.
   */
  beat: t.oneOfType([t.bool, t.number]),
  /**
   * Adds a whirl animation to the icon. If a number value is set, it represents
   * the iteration count of the animation.
   */
  whirl: t.oneOfType([t.bool, t.number]),
};
